import { createFileRoute } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/lib/auth";
import { CheckCircle2, Clock, XCircle, Trophy, CalendarIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const Route = createFileRoute("/_authenticated/bets")({
  head: () => ({ meta: [{ title: "My Bets — Brothers Club" }] }),
  component: BetsPage,
});

type Filter = "today" | "yesterday" | "custom" | "range";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function yesterdayISO() { return new Date(Date.now() - 86400000).toISOString().slice(0, 10); }

function BetsPage() {
  const { data: profile } = useProfile();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("today");
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [gameFilter, setGameFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { dateFrom, dateTo } = useMemo(() => {
    if (filter === "today") return { dateFrom: todayISO(), dateTo: todayISO() };
    if (filter === "yesterday") return { dateFrom: yesterdayISO(), dateTo: yesterdayISO() };
    if (filter === "custom") {
      const d = customDate ? format(customDate, "yyyy-MM-dd") : todayISO();
      return { dateFrom: d, dateTo: d };
    }
    // range
    const f = fromDate ? format(fromDate, "yyyy-MM-dd") : todayISO();
    const t = toDate ? format(toDate, "yyyy-MM-dd") : f;
    return { dateFrom: f, dateTo: t };
  }, [filter, customDate, fromDate, toDate]);

  const { data: games } = useQuery({
    queryKey: ["games_list_all"],
    queryFn: async () => {
      const { data } = await supabase.from("games").select("id, name, color").order("position");
      return data ?? [];
    },
  });

  const { data: rows } = useQuery({
    queryKey: ["my_bets", profile?.id, dateFrom, dateTo, gameFilter, statusFilter],
    enabled: !!profile,
    queryFn: async () => {
      let q = supabase
        .from("bets")
        .select("*, games(name, color)")
        .eq("user_id", profile!.id)
        .gte("bet_date", dateFrom)
        .lte("bet_date", dateTo)
        .order("created_at", { ascending: false });
      if (gameFilter !== "all") q = q.eq("game_id", gameFilter);
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      const { data } = await q;
      return data ?? [];
    },
  });

  // Fetch results for the shown date range/games to display winning number
  const { data: results } = useQuery({
    queryKey: ["results_range", dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await supabase
        .from("results")
        .select("game_id, result_date, result_value")
        .gte("result_date", dateFrom)
        .lte("result_date", dateTo);
      return data ?? [];
    },
  });

  const resultMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of results ?? []) m.set(`${r.game_id}_${r.result_date}`, r.result_value);
    return m;
  }, [results]);

  useEffect(() => {
    if (!profile) return;
    const ch = supabase
      .channel("my-bets-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "results" }, () => {
        qc.invalidateQueries({ queryKey: ["my_bets"] });
        qc.invalidateQueries({ queryKey: ["results_range"] });
      })
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "bets", filter: `user_id=eq.${profile.id}` },
        () => qc.invalidateQueries({ queryKey: ["my_bets"] }),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc, profile]);


  // Group by game + date
  const groups = useMemo(() => {
    const map = new Map<string, { game: any; bet_date: string; bets: any[] }>();
    for (const b of rows ?? []) {
      const key = `${b.game_id}_${b.bet_date}`;
      if (!map.has(key)) map.set(key, { game: b.games, bet_date: b.bet_date, bets: [] });
      map.get(key)!.bets.push(b);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ id, ...v }));
  }, [rows]);

  return (
    <div>
      <AppHeader />
      <div className="gradient-header px-4 pb-6 pt-2 -mt-1">
        <h2 className="text-lg font-black text-primary-foreground">My Bets</h2>
        <p className="text-xs text-white/70">All numbers you have played.</p>
      </div>

      <div className="space-y-3 px-4 py-4">
        {/* Date filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={filter === "today"} onClick={() => setFilter("today")}>Today</FilterChip>
          <FilterChip active={filter === "yesterday"} onClick={() => setFilter("yesterday")}>Yesterday</FilterChip>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 rounded-full border-0 px-3 text-xs font-bold",
                  filter === "custom" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
                )}
              >
                <CalendarIcon className="mr-1 h-3.5 w-3.5" />
                {filter === "custom" && customDate ? format(customDate, "dd MMM yyyy") : "Custom Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={customDate}
                onSelect={(d) => {
                  if (!d) return;
                  setCustomDate(d);
                  setFilter("custom");
                  setPopoverOpen(false);
                }}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
          <FilterChip active={filter === "range"} onClick={() => setFilter("range")}>Date Range</FilterChip>
        </div>

        {filter === "range" && (
          <div className="flex flex-wrap items-center gap-2">
            <Popover open={fromOpen} onOpenChange={setFromOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 rounded-full px-3 text-xs">
                  <CalendarIcon className="mr-1 h-3.5 w-3.5" />
                  From: {fromDate ? format(fromDate, "dd MMM") : "—"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={fromDate} onSelect={(d) => { if (d) { setFromDate(d); setFromOpen(false); } }} initialFocus className="p-3 pointer-events-auto" />
              </PopoverContent>
            </Popover>
            <Popover open={toOpen} onOpenChange={setToOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 rounded-full px-3 text-xs">
                  <CalendarIcon className="mr-1 h-3.5 w-3.5" />
                  To: {toDate ? format(toDate, "dd MMM") : "—"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={toDate} onSelect={(d) => { if (d) { setToDate(d); setToOpen(false); } }} initialFocus className="p-3 pointer-events-auto" />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Game + Status filters */}
        <div className="grid grid-cols-2 gap-2">
          <select
            value={gameFilter}
            onChange={(e) => setGameFilter(e.target.value)}
            className="h-9 rounded-lg border border-border bg-card px-2 text-xs font-semibold"
          >
            <option value="all">All Games</option>
            {(games ?? []).map((g: any) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-lg border border-border bg-card px-2 text-xs font-semibold"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>


        {/* Grouped bets */}
        {groups.length === 0 && (
          <div className="rounded-2xl bg-card p-8 text-center text-sm text-muted-foreground shadow-card">
            No bets found for selected filters.
          </div>
        )}

        {groups.map((g) => {
          const winNumber = resultMap.get(`${g.bets[0].game_id}_${g.bet_date}`);
          return (
            <div key={g.id} className="overflow-hidden rounded-2xl bg-card shadow-card">
              <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span
                    className="grid h-8 w-8 place-items-center rounded-lg text-sm font-black text-white"
                    style={{ background: g.game?.color ?? "var(--color-primary)" }}
                  >
                    {g.game?.name?.[0] ?? "?"}
                  </span>
                  <div>
                    <p className="text-sm font-bold leading-tight">{g.game?.name ?? "Game"}</p>
                    <p className="text-[10px] text-muted-foreground">{g.bets.length} bet{g.bets.length !== 1 ? "s" : ""} · {g.bet_date}</p>
                  </div>
                </div>
                <div className="text-right">
                  {winNumber != null ? (
                    <p className="text-[10px] font-bold text-success">Result: {String(winNumber).padStart(2, "0")}</p>
                  ) : (
                    <p className="text-[10px] font-semibold text-muted-foreground">Result awaited</p>
                  )}
                </div>
              </div>
              <div className="divide-y">
                {g.bets.map((b) => <BetRow key={b.id} b={b} color={g.game?.color} winNumber={winNumber} />)}
              </div>
            </div>
          );
        })}
        <div className="h-4" />
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "h-8 rounded-full px-3 text-xs font-bold transition-colors",
        active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function BetRow({ b, color, winNumber }: { b: any; color?: string; winNumber?: string }) {
  const StatusIcon = b.status === "won" ? Trophy
    : b.status === "pending" ? Clock
    : b.status === "lost" ? XCircle
    : CheckCircle2;
  const statusColor =
    b.status === "won" ? "text-success"
    : b.status === "pending" ? "text-warning"
    : b.status === "lost" ? "text-destructive"
    : "text-muted-foreground";

  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-sm font-black text-white"
          style={{ background: color ?? "var(--color-primary)" }}
        >
          {String(b.number).padStart(2, "0")}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">Number {String(b.number).padStart(2, "0")}</p>
          <p className="text-[11px] text-muted-foreground">
            {new Date(b.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
          </p>
          {winNumber != null && (
            <p className="text-[10px] font-semibold text-muted-foreground">Result: {String(winNumber).padStart(2, "0")}</p>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-black text-primary">₹{Number(b.amount).toLocaleString("en-IN")}</p>
        {Number(b.payout) > 0 && (
          <p className="text-[11px] font-bold text-success">Won ₹{Number(b.payout).toLocaleString("en-IN")}</p>
        )}
        <span className={cn("inline-flex items-center gap-1 text-[10px] font-semibold uppercase", statusColor)}>
          <StatusIcon className="h-3 w-3" /> {b.status}
        </span>
      </div>
    </div>
  );
}
